import * as Yup from 'yup';
import { identityApi, type CheckUserExistanceResType, type UserResponse } from '@commerce-identity-webcomponents/sdk';
import dayjs, { FormatObject } from 'dayjs';
import dayJsParsePlugin from 'dayjs/plugin/customParseFormat';
import {
  DynamicObjectInterface,
  profileApi,
  RegistrationResponse,
  PersonalDetailsType,
  PersonalIdRequestTypeEnum,
  SponsorSearchResponseInner,
  RegistrationFlowDataType,
  RegFlowConfig,
  CustomRequestOptions,
  ProfileConstantsEnum,
  FieldType,
  DocumentImageDataWithTypeTypeEnum,
  RoutePaths,
  RegistrationStatus,
  Projection,
  ValidationsForType,
  Contact,
  RegistrationSessionDataType,
  DocumentImageDataWithType,
  FunctionType,
} from '@commerce-profile-webcomponents/sdk';
import { Logger, StorageService } from '@nextgen-web-framework/core';
import { AccountType } from '@nextgen-web-framework/all';
import { AdvanceShareCodeResponse } from '@commerce-webcomponents/sdk';
import { IFieldVisibility, StepType } from '../components/Registration/types/propTypes';
import { RegistrationUserCheckType } from '../components/SinglePageRegistrationStep/types/propTypes';
import { RegistrationValidations } from '../components/RegistrationIdVerificationStep/types/propTypes';
import { GetRegistrationValidationDataByPkProps, GetRegistrationValidationDataProps } from './types/propTypes';
import {
  DATE_REGEX,
  ERROR_GENERIC_MESSAGE,
  FLOWS_WITH_PHASE_TWO,
  ProjectionParam,
  REG_FLOW_WISE_FIELD,
  SHAREBAR_FLOWS,
  TextConstants,
  VALIDATION_VALID_STRING,
} from './Constants';
import { defaultApiErrorHandler, parseObject, parseString, iff } from './helper';
import { UernameContactTypeEnum } from './types/enum';
import { getRegistration } from './accountLifeCycleUtils';

dayjs.extend(dayJsParsePlugin);
export const getJWTToken = async (
  username: string,
  password?: string,
  apiErrorHandler = defaultApiErrorHandler,
): Promise<string> => {
  const res: UserResponse = await apiErrorHandler(() =>
    identityApi.getJwtToken({
      username,
      password,
    }),
  );
  return res?.userToken || '';
};

const logger = Logger.getInstance();

export const checkUserExists = async (
  username: string,
  regPatchHandler: () => Promise<RegistrationResponse>,
  setIsUniqueProfile?: (isUniqu: boolean) => void,
): Promise<RegistrationUserCheckType> => {
  let profileResponse: DynamicObjectInterface = {},
    accountResponse: CheckUserExistanceResType = {};

  [profileResponse, accountResponse] = await Promise.all([
    regPatchHandler(),
    identityApi.checkUserExist({ username: username ?? '' }),
  ]);

  logger.log(profileResponse?.responseBody, accountResponse, 'profileResponse');
  const isUnique = !profileResponse?.responseBody?.code && !accountResponse?.isExist;

  setIsUniqueProfile?.(isUnique);
  return {
    isUnique: isUnique,
    usageStatus: profileResponse?.responseBody?.code ?? '',
  };
};

export const getRegistrationValidationData = ({ config, fieldNames }: GetRegistrationValidationDataProps) =>
  config.filter((field) => fieldNames.includes(field?.fieldName as string));
export const getRegistrationValidationDataByPk = ({ config, pk }: GetRegistrationValidationDataByPkProps) =>
  config.filter((field) => pk.includes(field?.pk as string));

export const registrationValidationSchema = (
  inputs: RegistrationValidations[],
  accountType: string,
  isForeignerSelected?: boolean,
  isCoApp = false,
): Yup.ObjectSchema<DynamicObjectInterface> => {
  const schemaObject: Record<string, DynamicObjectInterface> = {};

  inputs.forEach((input) => {
    // eslint-disable-next-line
    let fieldValidations: any;

    const field = input.fieldName;
    if (input.validations) {
      for (const [accountTypeKey, validationsForTypeValue] of Object.entries(input.validations as ValidationsForType)) {
        if (accountTypeKey === accountType) {
          fieldValidations = validationsForTypeValue;
        }
      }
    }

    if (!fieldValidations) {
      return;
    }

    let fieldSchema = Yup.string(); // Default schema for string fields

    if (fieldValidations.required) {
      fieldSchema = fieldSchema.required(`profile.validationMsg.${input.fieldName}.required`);
    } else {
      fieldSchema.nullable();
    }
    if (isForeignerSelected && fieldValidations.regex) {
      fieldSchema = fieldSchema.matches(
        fieldValidations.regex,
        `profile.validationMsg.foreigner${input.fieldName}.format`,
      );
    } else if (fieldValidations.regex) {
      fieldSchema = fieldSchema.matches(
        fieldValidations.regex,
        `profile.validationMsg.${input.fieldName}${isCoApp ? '.coapp' : ''}.format`,
      );
    }

    schemaObject[field as string] = fieldSchema;
  });

  return Yup.object().shape(schemaObject);
};
export const markAllFieldTouched = (values: PersonalDetailsType) => {
  const data = Object.keys(values).reduce((acc, key) => ({ ...acc, [key]: true }), {});
  return data;
};

export const replaceCoAppFieldValidations = (fields: RegistrationValidations[]) => {
  fields.forEach((field) => {
    field.validations = field?.COAPP?.validations ?? field?.validations ?? {};
  });
  return fields;
};

export const registrationValidationSchemafn = (
  inputs: RegistrationValidations[],
  accountType: string,
): Yup.ObjectSchema<DynamicObjectInterface> => {
  const schemaObject: Record<string, DynamicObjectInterface> = {};

  inputs.forEach((input) => {
    const { fieldName, validations, fieldType } = input;

    if (!validations) {
      return;
    }

    const fieldValidations = validations[accountType as keyof ValidationsForType] ?? validations['ALL'];

    if (!fieldValidations) {
      return;
    }

    let fieldSchema: Yup.StringSchema | Yup.BooleanSchema | Yup.ObjectSchema<DynamicObjectInterface> = Yup.string();

    if (fieldValidations?.visible !== false) {
      if ((fieldType == FieldType.CHECKBOX || fieldType == FieldType.CUSTOM) && fieldValidations.required) {
        fieldSchema = Yup.boolean()
          .required(`profile.validationMsg.${fieldName}.required`)
          .oneOf([true], `profile.validationMsg.${fieldName}.required`);
      } else if (fieldType == FieldType.FILE && fieldValidations.required) {
        fieldSchema = Yup.object()
          .shape({
            [DocumentImageDataWithTypeTypeEnum.Front]: Yup.boolean()
              .oneOf([true])
              .required(`profile.validationMsg.${fieldName}.required`),
            [DocumentImageDataWithTypeTypeEnum.Back]: Yup.boolean()
              .oneOf([true])
              .required(`profile.validationMsg.${fieldName}.required`),
          })
          .test(
            'bothRequired',
            'Both front and back must be true',
            (value) =>
              value &&
              value[DocumentImageDataWithTypeTypeEnum.Front] === true &&
              value[DocumentImageDataWithTypeTypeEnum.Back] === true,
          );
      } else if (fieldType == FieldType.DYNAMIC) {
        fieldSchema = Yup.string().test(`${fieldName}Validator` as string, '', function (value) {
          if (value === VALIDATION_VALID_STRING || (!fieldValidations?.required && !value)) {
            return true;
          } else if (fieldValidations?.required && value === undefined) {
            return this.createError({ message: '' });
          }
          return this.createError({ message: value });
        });
      } else if (fieldValidations?.required) {
        fieldSchema = Yup.string();
        fieldSchema = fieldSchema.required(`profile.validationMsg.${fieldName}.required`);
      }

      if (fieldValidations?.regex) {
        fieldSchema = (fieldSchema as Yup.StringSchema).matches(
          new RegExp(fieldValidations.regex),
          `profile.validationMsg.${fieldName}.format`,
        );
      }
    }
    const fieldNameUpdated = fieldType == FieldType.DYNAMIC ? `${fieldName}Validator` : fieldName;
    schemaObject[fieldNameUpdated as string] = fieldSchema;
  });

  return Yup.object().shape(schemaObject);
};

export const getRegistrationOptions = (inputs: RegistrationValidations[], role: string): Array<string> => {
  // eslint-disable-next-line
  let fieldOptions: any;
  inputs.forEach((input) => {
    const validationTypes = input.options;
    for (const [key, value] of Object.entries(validationTypes as ValidationsForType)) {
      if (key === role) {
        fieldOptions = value;
      }
    }
  });
  return fieldOptions as unknown as Array<string>;
};
const getloginUrlState = (url: string) =>
  btoa(
    JSON.stringify({
      redirectUri: url,
      headers: ApiConfigService.getTuples(),
    }),
  );

export const transformValidationArrayIntoObj = (
  arr: RegistrationValidations[] = [],
  key: string,
): RegistrationValidations => {
  const result = arr.reduce((acc: RegistrationValidations, item) => {
    if (Object.prototype.hasOwnProperty.call(item, key)) {
      acc[item[key as string] as string] = item;
    }
    return acc;
  }, {} as RegistrationValidations);
  return result;
};

export const getLoginRedirectProps = (sourceUrl: string) => {
  const { NEXT_PUBLIC_GLU_CLIENT_ID, NEXT_PUBLIC_BASE_PATH } = ConfigService.getConfigs();
  const defaultLoginIdentityProps = {
    scope: 'openid',
    state: getloginUrlState(sourceUrl),
    clientId: `${NEXT_PUBLIC_GLU_CLIENT_ID}`,
    redirectUri: `${NEXT_PUBLIC_BASE_PATH}/comgluuoidc/v1/auth`,
    acrValues: 'basic_jans_key',
    // Add for new version
    gluuBaseUrl: '',
    responseType: 'code',
    prompt: '',
    loginHint: '',
  };
  return defaultLoginIdentityProps;
};

export const loginAndRedirectAfterRegistration = (
  bcAuthToken: string | undefined,
  sourceUrl: string,
  apiErrorHandler = defaultApiErrorHandler,
): void => {
  const { NEXT_PUBLIC_GLU_BASE_URL, NEXT_PUBLIC_TENANT } = ConfigService.getConfigs();
  if (bcAuthToken && NEXT_PUBLIC_TENANT !== '200') {
    const tuple = ApiConfigService.getTuples();
    const loginProps = getLoginRedirectProps(sourceUrl);

    const getAuthorizeUrlPayload = {
      ...loginProps,
      countryIso: tuple['X-Amway-Country'],
      gluuBaseUrl: NEXT_PUBLIC_GLU_BASE_URL as string,
      acrValues: 'BCAuthn',
      bcAuthnToken: bcAuthToken,
      state: getloginUrlState(sourceUrl),
    };

    apiErrorHandler(() =>
      identityApi.getAuthorizeUrl(getAuthorizeUrlPayload).then((res) => {
        window.location.href = res;
      }),
    );
  }
};

export const getMinMaxAge = (inputs: RegistrationValidations[], role: string) => {
  const minMaxVal = {
    min: 0,
    max: 0,
  };
  inputs.forEach((input) => {
    const validationTypes = input.validations;
    for (const [key, value] of Object.entries(validationTypes as ValidationsForType)) {
      if (key === role) {
        if (value.min) {
          minMaxVal.min = value.min;
        }
        if (value.max) {
          minMaxVal.max = value.max;
        }
      }
    }
  });
  return minMaxVal;
};

export function validateDOB(
  date: string,
  selectedAccountType: AccountType,
  registrationFieldsMasterData: RegistrationValidations[] = [],
  isLunar: boolean, // true for thai member, abo
  isSpouse: boolean,
  t: DynamicObjectInterface,
) {
  const dateInputs = ['birthDate', 'minorAge'];
  const datefields = getRegistrationValidationData({
    config: registrationFieldsMasterData,
    fieldNames: dateInputs,
  });

  const { min, max } = getMinMaxAge(datefields, selectedAccountType);
  const dateConfig = datefields.find((item) => item.pk === 'birthDate');
  const ageCalAtyearEnd = dateConfig?.validations?.[selectedAccountType as keyof ValidationsForType]?.ageCalAtyearEnd;
  const dobObj = { msg: '', additionalDocument: false };
  if (date) {
    const isValidDate = validateDateString(date, isLunar);

    if (!isValidDate) {
      dobObj.msg = t('profile.RegistrationPersonalDetailsStep.invalidDate');
      return dobObj;
    }

    const endOfYearAge = getAge(date, isLunar, true);
    const actualAge = getAge(date, isLunar, false);
    if (actualAge <= 0 || !DATE_REGEX.test(date) || actualAge > TextConstants.MAX_AGE) {
      dobObj.msg = t('profile.RegistrationPersonalDetailsStep.invalidDate');
      return dobObj;
    }

    if (ageCalAtyearEnd && endOfYearAge < min) {
      dobObj.msg = iff(
        isSpouse,
        t('profile.RegistrationPersonalDetailsStep.CoAppDateError', { age: min }),
        t('profile.RegistrationPersonalDetailsStep.AboDateError'),
      );

      return dobObj;
    }

    if ((ageCalAtyearEnd && endOfYearAge == min) || (actualAge >= min && actualAge <= max)) {
      dobObj.msg = '';
      dobObj.additionalDocument = true;
      return dobObj;
    }

    if (!ageCalAtyearEnd && actualAge < min) {
      dobObj.msg = t(`profile.RegistrationPersonalDetailsStep.memberDateError${isSpouse ? '.coapp' : ''}`, {
        age: min,
      });
      return dobObj;
    }
  }
  return dobObj;
}

export const getAge = (
  date: string,
  isLunar: boolean,
  calcAtEndOfYear: boolean,
  inputFormat: FormatObject | string = 'DD/MM/YYYY', // Adding default format
) => {
  let dobObj = dayjs(date, inputFormat);
  if (isLunar) {
    dobObj = dobObj.subtract(543, 'year');
  }
  const currentDate = new Date();
  const endOYear = dayjs(new Date(currentDate.getFullYear(), 12, 0));
  const dateToCalcAge = calcAtEndOfYear ? endOYear : dayjs(currentDate);
  const age: number = dateToCalcAge.diff(dobObj, 'years');
  return age;
};

export const convertLunarToDate = (lunarDate: string) => {
  const [day, month, year] = lunarDate.split('/').map(Number);
  const dateCopy = new Date();
  dateCopy.setFullYear(year - 543);
  dateCopy.setMonth(month - 1);
  dateCopy.setDate(day);
  return dateCopy;
};

export const convertDateToLunar = (date: Date) => {
  const dateCopy = new Date(date);
  const day = (('0' as string) + dateCopy.getDate()).slice(-2);
  const month = (('0' as string) + (dateCopy.getMonth() + 1)).slice(-2);
  const year = dateCopy.getFullYear() + 543;
  const lunarDate = `${day}/${month}/${year}`;
  return lunarDate;
};

export const validateDateString = (dateStr: string, isLunar: boolean = false): boolean => {
  const [day, month, year] = dateStr.split('/').map(Number);
  const yearToUse = isLunar ? year - 543 : year;

  const dt = new Date(yearToUse, month - 1, day);

  // validation: check if parsed date matches original input
  if (dt.getFullYear() === yearToUse && dt.getMonth() === month - 1 && dt.getDate() === day) {
    return true;
  }

  return false;
};

export const getRegistrationFlow = (
  configData: DynamicObjectInterface,
  flow: DynamicObjectInterface,
  accountType = AccountType.BusinessOwner,
  isAnonymous = false,
) => {
  if (configData) {
    const { [flow]: currentFlow } = configData;
    if (FLOWS_WITH_PHASE_TWO.includes(flow)) {
      const steps: { [key: string]: StepType } = currentFlow?.steps;
      const filterKey = isAnonymous ? 'showForAnonymous' : 'showForLoggedIn';
      const updatedStep: { [key: string]: StepType } = {};
      if (steps) {
        Object.keys(steps)?.forEach((stepName) => {
          const accountConfig = steps?.[stepName]?.config?.[accountType];
          const stepFlag = accountConfig?.find((configData) => configData.key === filterKey)?.value;
          if (stepFlag === 'true') {
            updatedStep[stepName] = steps?.[stepName];
          }
        });
        currentFlow.steps = updatedStep;
      }
    }
    return currentFlow;
  }
  return {};
};

export const getPersonalDetailData = (
  res: RegistrationResponse,
  recordType?: string,
): Partial<PersonalDetailsType> | null => {
  let updatedPersonalDetails: Partial<PersonalDetailsType> = {
    isFieldDisabled: false,
  };
  if (recordType !== PersonalIdRequestTypeEnum.Passport) {
    updatedPersonalDetails = {
      ...updatedPersonalDetails,
      firstName: res.givenName,
      lastName: res.familyName,
      birthDate: res.birthDate ? convertDateToLunar(res.birthDate as Date) : '',
    };
  }
  return updatedPersonalDetails;
};
export const convertToUTCDate = (dateString: string, isLunar: boolean) => {
  const [day, month, year] = dateString.split('/').map(Number);
  const updatedMonth = month.toString().length === 1 ? `0${month}` : month;
  const updatedDay = day.toString().length === 1 ? `0${day}` : day;
  const updatedYear = isLunar ? year - 543 : year;
  return `${updatedYear}-${updatedMonth}-${updatedDay}`;
};
export const updateSponsor = (res: RegistrationResponse) => {
  let autoAssignSponsor: SponsorSearchResponseInner = {};
  if (res.sponsor && res.sponsorLocation) {
    autoAssignSponsor = {
      accountName: res.sponsorName,
      accountNumber: res.sponsor,
      location: res.sponsorLocation,
    };
  }
  return autoAssignSponsor;
};

export const convertDateObject = (date: Date) => dayjs(date);

export const checkIfDateIsFormatted = (date: string, format: string): boolean => {
  const parsedDate = dayjs(date, format, true);
  return parsedDate.isValid();
};

export const generateCustomHeaders = (
  regFlowData: RegistrationFlowDataType,
  challengeId = '',
): CustomRequestOptions => {
  if (regFlowData?.flowName === RegFlowConfig.CoApplicantFlow) {
    // Add only for co-app flow and without login (co-app phase 2)
    const { cidVerification } = regFlowData;
    const { spousePersonalId } = cidVerification ?? { spousePersonalId: '' };

    if (spousePersonalId) {
      const tokenValue = btoa(`${RegFlowConfig.CoApplicantFlow}-${spousePersonalId}`);
      return {
        headers: {
          'X-Auth-Token': tokenValue,
        },
      };
    }
  }
  if (challengeId) {
    return {
      headers: {
        'X-Amway-Regtkn': challengeId,
      },
    };
  }
  // default no headers
  return {
    headers: {},
  };
};

export const updateSponsorAttribute = (
  autoAssignSponsor: SponsorSearchResponseInner,
  sponsorName?: string,
  sponsor?: SponsorSearchResponseInner,
) => {
  if (sponsor?.accountNumber) {
    if (sponsorName) {
      return { ...sponsor, accountName: sponsorName };
    } else {
      return sponsor;
    }
  } else {
    return autoAssignSponsor;
  }
};

export const isValidSpouseVerification = (verifySpouseOnly: boolean, res: DynamicObjectInterface): boolean =>
  !!(
    (!verifySpouseOnly && res?.spousePersonalId?.recordNumber) || // Standard registration
    (verifySpouseOnly && res?.personalId?.recordNumber)
  ); // Co-App

//Error message fallback
export const getErrorMessageForKey = (key: string, t: FunctionType, fallback: string) => {
  try {
    const specificValue: unknown = t(key);
    if (specificValue === key) {
      return t(fallback);
    } else {
      return specificValue;
    }
  } catch (error) {
    logger.info(error, 'error');
    return fallback;
  }
};

export const getErrorTranslationMsg = (key: string, t: DynamicObjectInterface, fallback?: string) => {
  try {
    let message = t(key);
    if (message === key && fallback) {
      message = t(fallback);
    }
    if (message === fallback) {
      message = t(ERROR_GENERIC_MESSAGE.body);
    }
    return message;
  } catch (error) {
    logger.info(error, 'error');
    return t(ERROR_GENERIC_MESSAGE.body);
  }
};

export const sponsorFailedHandler = (flowName: string, callback?: FunctionType) => {
  if (flowName !== RegFlowConfig.SelfInitiatedFlow) {
    const localstorage = StorageService.getLocalStorage();
    const sharecodeAttributes = localstorage.retrieve(
      ProfileConstantsEnum.shareCodeAttributes,
    ) as AdvanceShareCodeResponse;
    delete sharecodeAttributes.accountId;
    localstorage.store(ProfileConstantsEnum.shareCodeAttributes, sharecodeAttributes);
  }
  callback?.();
};

export const addressInputCallbackForRegistration = (contact?: Contact, callback?: FunctionType) => {
  if (contact) {
    const tuples = ApiConfigService.getTuples();
    const countryIso = tuples['X-Amway-Country']?.toUpperCase();
    contact.countryCode = countryIso;
    callback?.(contact);
  }
};

export const deleteRegistrationInfoFromSession = () => {
  const sessionstorage = StorageService.getSessionStorage();
  sessionstorage.remove(ProfileConstantsEnum.registrationInfoKey);
};

export const storeRegistrationInfoToSession = (registrationId: string, challengeId: string, currentIndex: number) => {
  const { REGISTRATION_REFRESH_RETAIN = true } = ConfigService.getConfigs();
  const sessionstorage = StorageService.getSessionStorage();
  if (REGISTRATION_REFRESH_RETAIN) {
    sessionstorage.store(ProfileConstantsEnum.registrationInfoKey, {
      registrationId,
      challengeId,
      currentIndex,
    });
  }
};

export const storeRegistrationStepBack = (nextIndex: number) => {
  const { REGISTRATION_REFRESH_RETAIN = true } = ConfigService.getConfigs();
  if (REGISTRATION_REFRESH_RETAIN) {
    const sessionstorage = StorageService.getSessionStorage();
    const sessionData = sessionstorage.retrieve(
      ProfileConstantsEnum.registrationInfoKey,
    ) as unknown as RegistrationSessionDataType;
    if (sessionData) {
      sessionData.currentIndex = nextIndex;
      sessionstorage.store(ProfileConstantsEnum.registrationInfoKey, sessionData);
    }
  }
};

export const fetchPersonalIdAndDocument = (personalIdList: DynamicObjectInterface) => {
  const recordData = personalIdList?.find((idRecord: DynamicObjectInterface) => idRecord?.recordNumber);
  const documentDataListComplete = personalIdList?.filter(
    (idRecord: DynamicObjectInterface) => idRecord?.documentDataList?.length !== 0,
  );
  const documentDataList = documentDataListComplete?.reduce(
    (
      accumulatedData: DocumentImageDataWithType[],
      currentData: DynamicObjectInterface,
    ): DocumentImageDataWithType[] => {
      const newDataList = currentData?.documentDataList?.filter(
        (docData: DocumentImageDataWithType) => !!docData?.documentData,
      );
      return [...accumulatedData, ...newDataList];
    },
    [],
  );
  return [recordData, documentDataList];
};

export const restoreRegistrationDataFromSession = async (
  steps: StepType[],
  initCallback: FunctionType,
  setSubmitOnLastStep: FunctionType,
  countryDialCode = '',
) => {
  const { REGISTRATION_REFRESH_RETAIN = true } = ConfigService.getConfigs();
  if (!REGISTRATION_REFRESH_RETAIN) {
    initCallback({});
    return;
  }
  let initData = {};

  const sessionstorage = StorageService.getSessionStorage();
  const sessionData = sessionstorage.retrieve(
    ProfileConstantsEnum.registrationInfoKey,
  ) as unknown as RegistrationSessionDataType;
  if (!sessionData?.registrationId || sessionData?.currentIndex >= steps.length) {
    deleteRegistrationInfoFromSession();
    initCallback({});
    return;
  }
  const response: DynamicObjectInterface = await profileApi.getRegistrationDetailsById(
    sessionData?.registrationId,
    {
      headers: { 'X-Amway-Regtkn': sessionData?.challengeId },
    },
    `${ProjectionParam.FULL},${ProjectionParam.DOCUMENTS}`,
  );
  const coapplicantDataResp = response?.coapplicantData as DynamicObjectInterface;
  const [recordData, documentDataList] = fetchPersonalIdAndDocument(response?.personalIdList);
  const flowName = response?.registrationFlow;
  const initialStep = steps?.[sessionData.currentIndex];
  const aboKitVerified = Boolean(response?.prePrintedKitId);
  const usernameContactType = response?.usernameContactType ?? (UernameContactTypeEnum.Email as UernameContactTypeEnum);
  const phoneWithcountryCode = response?.phone?.replace(countryDialCode, '');
  const username = usernameContactType === UernameContactTypeEnum.Phone ? phoneWithcountryCode : response?.email;
  const internationalSponsor: DynamicObjectInterface = response?.internationalSponsor ?? {};
  let internationalDetails: DynamicObjectInterface = {};
  if (Object.keys(internationalSponsor).length !== 0) {
    internationalSponsor['accountName'] = internationalSponsor?.sponsorName;
    internationalSponsor['location'] = response?.sponsorLocation;
    internationalDetails = {
      internationalSponsorCountryCode: internationalSponsor?.countryCode,
      internationalSponsorTncCheckbox: true,
      internationalSponsorType: 'international',
      internationalSposnorVerified: true,
      isInternational: true,
      setInternationalSponsor: true,
    };
  }
  let coAppPersonalDetails = {};
  const coAppPersonalId: string[] = Object.keys(coapplicantDataResp ?? {});
  if (coAppPersonalId[0]) {
    const coappData: DynamicObjectInterface = coapplicantDataResp[coAppPersonalId[0]];
    const usernameContactType =
      coappData?.usernameContactType ?? (UernameContactTypeEnum.Email as UernameContactTypeEnum);
    const phoneWithcountryCode = coappData?.phone?.replace(countryDialCode, '');
    const username = usernameContactType === UernameContactTypeEnum.Phone ? phoneWithcountryCode : coappData?.email;
    const [recordData, documentDataList] = fetchPersonalIdAndDocument(coappData?.personalIdList);
    coAppPersonalDetails = {
      coAppRegistrationId: coAppPersonalId[0],
      firstName: coappData?.givenName,
      lastName: coappData?.familyName,
      userName: username,
      birthDate: coappData?.birthDate,
      gender: coappData?.gender,
      email: coappData?.email,
      phone: coappData?.phone,
      userNameValidator: VALIDATION_VALID_STRING,
      secondaryContactValidator: VALIDATION_VALID_STRING,
      dobValidator: VALIDATION_VALID_STRING,
      personalIdType: recordData?.recordType,
      personalIdRecordNumber: recordData?.recordNumber,
      citizenshipCountry: coappData?.citizenshipCountry,
      documentDataList,
      personalIdDocument: documentDataList?.length == 2 ? { FRONT: true, BACK: true } : undefined,
    };
  }
  initData = {
    mainData: {
      currentIndex: sessionData.currentIndex,
      currentStepName: initialStep?.stepName,
      isRefreshRestoredData: response?.registrationFlow !== RegFlowConfig.CoApplicantFlow,
      phonePrimaryContactSession: usernameContactType === UernameContactTypeEnum.Phone,
      aboKitVerified: aboKitVerified,
      doNotKnowSponsor: response?.doNotKnowSponsor,
      documentDataList,
    },
    accountType: response?.accountType,
    userData: {
      registrationId: sessionData?.registrationId,
      patchVersion: response?.version,
    },
    OtpData: {
      challengeId: sessionData?.challengeId,
      isOTPVerified: true,
    },
    perosonalDetails: {
      isFieldDisabled: false,
      firstName: response?.givenName,
      lastName: response?.familyName,
      userName: username,
      birthDate: response?.birthDate,
      dobValidator: VALIDATION_VALID_STRING,
      gender: response?.gender,
      email: response?.email,
      phone: response?.phone,
      userNameValidator: VALIDATION_VALID_STRING,
      secondaryContactValidator: VALIDATION_VALID_STRING,
      personalIdType: recordData?.recordType,
      personalIdRecordNumber: recordData?.recordNumber,
      taxRegime: response?.taxRegime,
      citizenshipCountry: response?.citizenshipCountry,
      createPassword: '*********',
      password: '*********',
      termsAndConditionCheckbox: true,
      address: {
        addrLineOne: '',
        addrLineTwo: '',
        state: '',
        city: '',
        postalCode: '',
        province: '',
        provinceCode: '',
        district: '',
        subDistrict: '',
        countryCode: '',
        ...response?.billingAddress,
      },
      personalIdDocument: documentDataList?.length == 2 ? { FRONT: true, BACK: true } : undefined,
    },
    coAppPersonalDetails,
    selectSponsor: {
      editSponsor: flowName !== RegFlowConfig.ShareBarFlow,
      sponsor: {
        accountName: response?.sponsorName,
        accountNumber: response?.sponsor,
        location: response?.sponsorLocation,
      },
      internationalSponsor: response?.internationalSponsor,
      hideSponsorRadioSelection: true,
      ...internationalDetails,
    },
  };
  if (aboKitVerified) {
    setSubmitOnLastStep(true);
  }

  initCallback(initData);
};

export const getFieldVisibility = (
  registrationFieldsMasterData: RegistrationValidations[],
  key: string,
  selectedAccountType: AccountType,
  flowName: RegFlowConfig,
): IFieldVisibility => {
  const validationObject: DynamicObjectInterface = transformValidationArrayIntoObj(registrationFieldsMasterData, key);
  const fieldVisibility: IFieldVisibility = {};
  Object.keys(validationObject).forEach((fieldName) => {
    let { validations } = validationObject[fieldName as string] as RegistrationValidations;
    if (flowName === RegFlowConfig.CoApplicantFlow) {
      validations = validationObject?.[fieldName as string]?.['COAPP']?.validations as ValidationsForType;
    }
    const visible =
      validations?.[selectedAccountType as keyof ValidationsForType]?.visible ?? validations?.['ALL']?.visible;
    fieldVisibility[fieldName] = visible ?? false;
  });

  return fieldVisibility;
};

export const isRequiredKitSelector = (flowName: string) => {
  const { NEXT_PUBLIC_REGISTRATION_WITH_STARTER_KIT_FLOW } = ConfigService.getConfigs();
  const requiredFlows = Array.isArray(NEXT_PUBLIC_REGISTRATION_WITH_STARTER_KIT_FLOW)
    ? NEXT_PUBLIC_REGISTRATION_WITH_STARTER_KIT_FLOW
    : [];
  return requiredFlows?.includes(flowName);
};

export const startKitSelector = (setShowKitSelector: FunctionType) =>
  new Promise<DynamicObjectInterface>((resolve) => {
    setShowKitSelector(true);

    const handleKitSelection = (kit: string, fee?: string, rejected?: boolean) => {
      setShowKitSelector(false);
      resolve({ kit, fee, rejected });
    };

    (window as DynamicObjectInterface).handleSkuSelect = handleKitSelection;
  });

export const checkForSkipCart = async (
  flowName: string,
  redirectUrl: string,
  setShowKitSelector: DynamicObjectInterface,
  shareCodeCategory = SHAREBAR_FLOWS.MEPANEL,
) => {
  let starterKitSku = '';
  let feeSku = '';
  let useNewCartForCheckout = false;
  let returnUrl = redirectUrl;
  let rejected = false;
  if (isRequiredKitSelector(flowName)) {
    // Skip for product/link share
    if (flowName === RegFlowConfig.ShareBarFlow && redirectUrl === RoutePaths.CHECKOUT) {
      return { starterKitSku, useNewCartForCheckout, returnUrl };
    }

    const { kit, fee, rejected: isClosed } = await startKitSelector(setShowKitSelector);
    starterKitSku = kit;
    feeSku = fee;
    rejected = isClosed;

    // Flow to handle PDP, List, Shop flow without registration
    // If registration is selected then user will get CHECKOUT in URL and redirected from the above
    if (flowName === RegFlowConfig.ShareBarFlow && shareCodeCategory !== SHAREBAR_FLOWS.MEPANEL) {
      return { starterKitSku, feeSku, useNewCartForCheckout, returnUrl, rejected };
    }

    // Skip Cart and modify return URL to checkout
    returnUrl = RoutePaths.CHECKOUT;
    // Future enhancement, use config value instead of hard code
    if (redirectUrl === RoutePaths.CART) {
      // Assumption: if exiting to cart, then we need to create new cart
      useNewCartForCheckout = true;
    }
  }
  return { starterKitSku, feeSku, useNewCartForCheckout, returnUrl, rejected };
};

export const getExistingPartnerCID = async (accountNum: string, apiErrorHandler = defaultApiErrorHandler) => {
  // Get existing data from master regId
  const completedRegId = await getRegistration(undefined, accountNum, RegistrationStatus.Completed, true);

  let spouseCid = '';
  if (completedRegId) {
    const regRes = await apiErrorHandler(() =>
      profileApi.getRegistrationDetailsById(parseString(completedRegId), {}, Projection.Full),
    );
    const { spousePersonalId } = parseObject(regRes);

    spouseCid = parseString(spousePersonalId?.recordNumber);
  }
  // No error handling required, no record then will be defaulted to empty
  return spouseCid;
};

export const sanitizeInput = (value: string) => {
  let sanitized = value;
  if (sanitized?.startsWith(' ')) {
    sanitized = sanitized?.trimStart();
  }
  // Prevent multiple consecutive spaces by replacing 2+ spaces with 1 space, lets user type space, but not two spaces in a row
  sanitized = sanitized?.replace(/ {2,}/g, ' ');
  return sanitized;
};

export const getFlowValidation: DynamicObjectInterface = (flowName: RegFlowConfig) => {
  switch (flowName) {
    case RegFlowConfig.AboLedFlow:
      return REG_FLOW_WISE_FIELD.aboLedFlow;
    case RegFlowConfig.CoApplicantFlow:
      return REG_FLOW_WISE_FIELD.coApplicantFlow;
    default:
      return REG_FLOW_WISE_FIELD.selfInitiatedFlow;
  }
};

export const updateCoApplicantPayload: DynamicObjectInterface = (
  payload: DynamicObjectInterface,
  coAppRegistrationId: string,
) => {
  const coAppData: DynamicObjectInterface = { version: payload.version };
  delete payload.version;
  if (coAppRegistrationId) {
    coAppData['coapplicantData'] = {
      [coAppRegistrationId]: {
        ...payload,
      },
    };
  }
  return coAppData;
};

export const updateResponseCodeWithTealium = (code: string): string => {
  if (!code) {
    return '';
  }

  return code.startsWith('comregistrationas') ? code.replace(/comregistrationas/g, 'registration') : code;
};
